import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import { useGT } from 'gt-react';
import { useToast } from '@/context/ToastContext';
import type {
  CreateReviewExclusionRuleInput,
  ExtractEmailArchiveMembersInput,
  LabelingMailboxFilter,
  LabelingQueueKind,
  ListReviewClaimsInput,
  PreviewParseFixInput,
  PreviewClaimLinkConfigurationInput,
  PreviewStatedDecisionInput,
  PreviewRejectionDetailInput,
  SubmitReviewSessionInput,
} from '@erp/rpc';

/**
 * Show a localized failure, and keep the raw server text for support.
 *
 * The server's messages are diagnostics ("Line index 7 is out of range for response
 * …") — accurate, English-only, and meaningless to a labeler. Surfacing them verbatim
 * was both an i18n hole and bad UX, so the user gets one contextualized sentence and
 * the detail goes to the console.
 */
function useLabelingErrorToast() {
  const { showToast } = useToast();
  const gt = useGT();
  return (error: Error) => {
    console.error('[849 labeling] request failed', error);
    showToast({
      title: gt('Could not save', { context: 'labeling error toast title' }),
      message: gt('That did not go through. Try again in a moment.', {
        context: 'labeling error toast message',
      }),
      color: 'error',
    });
  };
}

/** Every labeling query, invalidated together after a label lands. */
function useInvalidateLabeling() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.listReviewQueue.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.listMailbox.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.listLabeled.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.counts.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.reviewQueueTotals.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.claimAging.key(),
    });
    // A parse fix adds a configuration: every pre-parse of the supplier and
    // its ranking read differently now (#7649).
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.getEmailPreparse.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.sdSuppliers.configRanking.key(),
    });
  };
}

/** The header's counts and dollars per queue (#7655). */
export function useReviewQueueTotals() {
  return useQuery(
    orpc.shipAndDebit.labeling.reviewQueueTotals.queryOptions({ input: {} }),
  );
}

/** Outstanding email-claim lines per supplier by age (#7655). */
export function useClaimAging() {
  return useQuery(
    orpc.shipAndDebit.labeling.claimAging.queryOptions({ input: {} }),
  );
}

export function useLabelingCounts() {
  return useQuery(
    orpc.shipAndDebit.labeling.counts.queryOptions({ input: {} }),
  );
}

/**
 * The Review queue: what the classifier escalated, likeliest first.
 *
 * `keepPreviousData` because the list re-fetches on every label and every skip, and
 * a queue that blanks between items is unusable at the pace this is worked at.
 */
export function useLabelingReviewQueue(input: {
  skipInboundEmailIds?: string[];
}) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.listReviewQueue.queryOptions({ input }),
    placeholderData: keepPreviousData,
  });
}

/** Browse mode: the whole mailbox with each email's label state. */
export function useLabelingMailbox(input: {
  page?: number;
  pageSize?: number;
  search?: string;
  filter?: LabelingMailboxFilter;
}) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.listMailbox.queryOptions({ input }),
    placeholderData: keepPreviousData,
  });
}

/** One email's labeling context. Disabled until a row is opened. */
export function useEmailForLabeling(inboundEmailId: string | null) {
  return useQuery(
    orpc.shipAndDebit.labeling.getEmailForLabeling.queryOptions({
      input: inboundEmailId ? { inboundEmailId } : skipToken,
    }),
  );
}

/**
 * The claim panel's list (#7645): the supplier's claims for one email, with the
 * engine's suggestions. Waits for a supplier — claims are scoped to one.
 */
export function useReviewClaims(params: {
  inboundEmailId: string;
  supplierId: string | null;
  enabled: boolean;
  /** The form's part roles: a supporting part is not read for evidence (#7647). */
  attachmentRoles?: ListReviewClaimsInput['attachmentRoles'];
  /** The body's role: a payload body leaves no unmarked file to read. */
  bodyRole?: ListReviewClaimsInput['bodyRole'] | null;
}) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.listReviewClaims.queryOptions({
      input:
        params.enabled && params.supplierId
          ? {
              inboundEmailId: params.inboundEmailId,
              supplierId: params.supplierId,
              attachmentRoles: params.attachmentRoles,
              ...(params.bodyRole ? { bodyRole: params.bodyRole } : {}),
            }
          : skipToken,
    }),
    // A role change re-asks with new roles; keep the list on screen meanwhile.
    placeholderData: keepPreviousData,
  });
}

/**
 * What a stated decision would do (#7651) — the lines, the decisions, and the
 * checksum against the email's totals, shown before the reviewer confirms. A
 * read: nothing is written until the session is submitted.
 */
export function useStatedDecisionPreview(
  input: PreviewStatedDecisionInput | null,
) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.previewStatedDecision.queryOptions({
      input: input ?? skipToken,
    }),
    // Typing a credit re-asks; keep the last answer on screen meanwhile.
    placeholderData: keepPreviousData,
  });
}

/**
 * Where a rejection-detail supplier's email would land (SD-10) — the rows on
 * EDI 849 lines, the rows left unplaced and why. A read: the attach is applied
 * when the session is submitted.
 */
export function useRejectionDetailPreview(
  input: PreviewRejectionDetailInput | null,
) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.previewRejectionDetail.queryOptions({
      input: input ?? skipToken,
    }),
    // The answer follows the server — an EDI 849 arriving, another attach —
    // so it is re-asked like any read; the last answer stays on screen meanwhile.
    placeholderData: keepPreviousData,
  });
}

/**
 * A parse fix's dry run (#7649): column picks or a plain-text fix turned into a
 * configuration and read on the same email. A plain-text fix asks the drafting
 * agent, so the answer on screen is kept rather than re-asked on focus.
 */
export function useParseFixPreview(input: PreviewParseFixInput | null) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.previewParseFix.queryOptions({
      input: input ?? skipToken,
    }),
    staleTime: Infinity,
    // Only the request on screen is kept: a draft left for another one (text
    // A, then B, then A again) asks again rather than serving A's failed or
    // declined answer.
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * The configuration the claim link will apply on the ticked claims (#7648) —
 * the parse view's "Will apply". A read: the report is placed, nothing written.
 */
export function useClaimLinkConfiguration(
  input: PreviewClaimLinkConfigurationInput | null,
) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.previewClaimLinkConfiguration.queryOptions({
      input: input ?? skipToken,
    }),
    // No placeholder: an answer for the previous selection must never read
    // as "Will apply" for the current one. Until the new answer is in, the
    // parse view falls back to the first configuration that reads rows.
  });
}

/**
 * The email's stored pre-parse (#7647) — a read of what the classifier's step
 * already parsed. Opening the review modal parses nothing.
 */
/**
 * The parse view's report. Keyed by the form's supplier: picking a supplier the
 * stored parse did not use re-parses under that supplier's configurations.
 */
export function useEmailPreparse(
  inboundEmailId: string,
  enabled: boolean,
  supplierId: string | null = null,
) {
  return useQuery(
    orpc.shipAndDebit.labeling.getEmailPreparse.queryOptions({
      input: enabled
        ? { inboundEmailId, ...(supplierId ? { supplierId } : {}) }
        : skipToken,
    }),
  );
}

export function useLabeledRecord(input: {
  kind: LabelingQueueKind;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.listLabeled.queryOptions({ input }),
    placeholderData: keepPreviousData,
  });
}

/**
 * The supplier on its own, saved the moment it is picked on the review form.
 * Re-reads the email afterwards so reopening it restores the pick.
 *
 * Scoped per email, so saves run one after another: two quick picks can never
 * land out of order and leave the first one saved.
 */
export function useSaveReviewSupplier(inboundEmailId: string) {
  const queryClient = useQueryClient();
  const showError = useLabelingErrorToast();
  return useMutation({
    scope: { id: `sd849-review-supplier:${inboundEmailId}` },
    mutationFn: (supplierId: string) =>
      rpcClient.shipAndDebit.labeling.saveReviewSupplier({
        inboundEmailId,
        supplierId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.labeling.getEmailForLabeling.key({
          input: { inboundEmailId },
        }),
      });
    },
    onError: showError,
  });
}

/**
 * The reviewer's note on a verdict already submitted (AIA-38), asked for on
 * the outcome after a confident disagreement.
 */
export function useSaveReviewNote(inboundEmailId: string) {
  const queryClient = useQueryClient();
  const showError = useLabelingErrorToast();
  return useMutation({
    mutationFn: (note: string) =>
      rpcClient.shipAndDebit.labeling.saveReviewNote({ inboundEmailId, note }),
    // The email's label changed: reopening it reads the label as saved.
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.labeling.getEmailForLabeling.key({
          input: { inboundEmailId },
        }),
      });
    },
    onError: showError,
  });
}

/**
 * The files inside a zip attached to an email. Fetched only once the zip is
 * opened: listing means downloading the archive on the server.
 */
export function useEmailArchiveMembers(
  target: { inboundEmailId: string; attachmentId: string } | null,
) {
  return useQuery(
    orpc.shipAndDebit.labeling.listEmailArchiveMembers.queryOptions({
      input: target ?? skipToken,
    }),
  );
}

/**
 * Attach the picked files of a zip to its email. Everything that lists or
 * reads the email's parts is refreshed, so the files show up as parts with a
 * role, in the viewer, and in the parse.
 */
export function useExtractEmailArchiveMembers() {
  const queryClient = useQueryClient();
  const showError = useLabelingErrorToast();
  return useMutation({
    mutationFn: (input: ExtractEmailArchiveMembersInput) =>
      rpcClient.shipAndDebit.labeling.extractEmailArchiveMembers(input),
    onSuccess: (_result, input) => {
      const { inboundEmailId } = input;
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.labeling.getEmailForLabeling.key({
          input: { inboundEmailId },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.labeling.listEmailArchiveMembers.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.labeling.getEmailPreparse.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.labeling.listReviewClaims.key(),
      });
      // The viewer's signed URLs come from the REST email read.
      void queryClient.invalidateQueries({
        queryKey: [`communications/inbound-emails/${inboundEmailId}`],
      });
    },
    onError: showError,
  });
}

/**
 * The review session (#7644): the verdict with its reasons, and the supplier, in
 * one call. Resolves with what was applied, recorded and learned, which the
 * review modal shows before moving on.
 */
export function useSubmitReviewSession() {
  const invalidate = useInvalidateLabeling();
  const showError = useLabelingErrorToast();
  return useMutation({
    mutationFn: (input: SubmitReviewSessionInput) =>
      rpcClient.shipAndDebit.labeling.submitReviewSession(input),
    onSuccess: invalidate,
    onError: showError,
  });
}

/**
 * Undo — returns a labeled item to its queue.
 *
 * Mandatory rather than polish: items vanish from the queue the moment they're
 * labeled, so a misclick has to be recoverable without hunting the Labeled page.
 */
export function useUndoLabel() {
  const invalidate = useInvalidateLabeling();
  const { showToast } = useToast();
  const gt = useGT();
  const showError = useLabelingErrorToast();
  return useMutation({
    mutationFn: (input: { kind: LabelingQueueKind; subjectId: string }) =>
      rpcClient.shipAndDebit.labeling.undo(input),
    onSuccess: (result) => {
      invalidate();
      // Undo only removes YOUR labels. Removing nothing means the label belongs to
      // another labeler — say so, or the button looks broken.
      if (result.removed === 0) {
        showToast({
          title: gt('Nothing to undo', {
            context: 'labeling undo toast title',
          }),
          message: gt(
            'This was labeled by someone else. Re-label it to record your own judgement.',
            { context: 'labeling undo toast message' },
          ),
          color: 'warning',
        });
      }
    },
    onError: showError,
  });
}

/** The rules that keep emails out of the review queue. */
export function useReviewExclusionRules() {
  return useQuery(
    orpc.shipAndDebit.labeling.listReviewExclusionRules.queryOptions({
      input: {},
    }),
  );
}

/**
 * Which of the queue's emails a rule being typed would hide. Null while the rule
 * names no pattern; the last answer stays on screen while the next is asked.
 */
export function useReviewExclusionRulePreview(
  input: { senderPattern: string | null; subjectPattern: string | null } | null,
) {
  return useQuery({
    ...orpc.shipAndDebit.labeling.previewReviewExclusionRule.queryOptions({
      input: input ?? skipToken,
    }),
    placeholderData: keepPreviousData,
  });
}

/** Adding or removing a rule changes the queue's membership: re-read all of it. */
function useInvalidateExclusionRules() {
  const queryClient = useQueryClient();
  const invalidateLabeling = useInvalidateLabeling();
  return () => {
    invalidateLabeling();
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.listReviewExclusionRules.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.shipAndDebit.labeling.previewReviewExclusionRule.key(),
    });
  };
}

export function useCreateReviewExclusionRule() {
  const invalidate = useInvalidateExclusionRules();
  const showError = useLabelingErrorToast();
  return useMutation({
    mutationFn: (input: CreateReviewExclusionRuleInput) =>
      rpcClient.shipAndDebit.labeling.createReviewExclusionRule(input),
    onSuccess: invalidate,
    onError: showError,
  });
}

export function useDeleteReviewExclusionRule() {
  const invalidate = useInvalidateExclusionRules();
  const showError = useLabelingErrorToast();
  return useMutation({
    mutationFn: (ruleId: string) =>
      rpcClient.shipAndDebit.labeling.deleteReviewExclusionRule({ ruleId }),
    onSuccess: invalidate,
    onError: showError,
  });
}
